import {
  Dimmer_SchedulerMode,
  Devices_ClockMechanism_ManualMoveMode,
  Usb_UsbPowerSchedulerMode
} from "../api";
import {BigClock_Domain_Time} from "../api/generated";
import {
  SensorBlockGpsCoordinates,
} from "../models/SensorBlock.model";
import {
  Coordinates,
  RelayTime,
} from "../components/menu/RelaySettings/RelaySettings.model";
import {TimeSynchronizationSource} from "../api";
import {
  ConnectedDevice,
  SystemDevices,
} from "../components/menu/ExternalDevices/components/AddExternalDevicesWidget/AddExternalDevicesWidget.model";
import {
  ElectronicClockData,
  ElectronicClockParams,
} from "../components/menu/ExternalDevices/components/ElectronicClock/ElectronicClock.model";
import {DimmerParams} from "../models/Dimmer.model";

const numberToSchedulerMode: Record<number, Usb_UsbPowerSchedulerMode> = {
  0: 'AlwaysOff',
  1: 'AlwaysOn',
  2: 'Schedule',
};

export const parseOffsetToTicks = (offset: string): number => {
  const match = String(offset).trim().match(/^([+-]?)(\d{1,2})(?::?(\d{2}))?$/);

  if (!match) {
    return 0;
  }

  const [, sign, hoursRaw, minutesRaw = "00"] = match;
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  const totalSeconds = (hours * 60 * 60) + (minutes * 60);
  const signedSeconds = sign === "-" ? -totalSeconds : totalSeconds;

  return signedSeconds * 10_000_000;
};

export const mapTimeValue = (time: string | Date): string => {
  if (time instanceof Date) {
    return time.toISOString();
  }

  return String(time);
};

export const mapEspSynchronizationSource = (
  synchronizationSource: string | number
): TimeSynchronizationSource => {
  if (synchronizationSource === 1 || synchronizationSource === "ClockStation") {
    return "ClockStation";
  }

  return "Ntp";
};

export const mapManualModeToContract = (mode: number): Devices_ClockMechanism_ManualMoveMode => {
  switch (mode) {
    case 1:
      return "Forward";
    case 2:
      return "Backward";
    default:
      return "Stop";
  }
};

export const mapUsbModeToContract = (mode: number | string): Usb_UsbPowerSchedulerMode => {
  const numericMode = Number(mode);

  return numberToSchedulerMode[numericMode] ?? 'AlwaysOff';
};

export const mapTimeStringToTimeSpan = (time: string): BigClock_Domain_Time => {
  const [hours = 0, minutes = 0, seconds = 0] = time
    .split(':')
    .map((part) => Number(part));

  return {
    hour: hours,
    minute: minutes,
    seconds,
  };
};

export const mapTimeSpanToTimeString = (time?: BigClock_Domain_Time | null): string => {
  const hours = String(time?.hour ?? 0).padStart(2, '0');
  const minutes = String(time?.minute ?? 0).padStart(2, '0');
  const seconds = String(time?.seconds ?? 0).padStart(2, '0');

  return `${hours}:${minutes}:${seconds}`;
};
export const mapSensorBlockGpsCoordinatesResponse = (
  response: Record<string, unknown>
): SensorBlockGpsCoordinates => ({
  status: (response.status as string | null | undefined) ?? null,
  latitudeDirection: (response.latitudeDirection as string | null | undefined) ?? null,
  latitudeDegrees: response.latitudeDegrees != null ? String(response.latitudeDegrees) : null,
  latitudeMinutes: response.latitudeMinutes != null ? String(response.latitudeMinutes) : null,
  latitudeMinuteHundredths: response.latitudeMinuteHundredths != null ? String(response.latitudeMinuteHundredths) : null,
  longitudeDirection: (response.longitudeDirection as string | null | undefined) ?? null,
  longitudeDegrees: response.longitudeDegrees != null ? String(response.longitudeDegrees) : null,
  longitudeMinutes: response.longitudeMinutes != null ? String(response.longitudeMinutes) : null,
  longitudeMinuteHundredths: response.longitudeMinuteHundredths != null ? String(response.longitudeMinuteHundredths) : null,
  coordinates: (response.coordinates as string | null | undefined) ?? null,
});

export const mapRelayTimeResponse = (response: Record<string, unknown>): RelayTime => {
  const directOnTime = response.onTime;
  const directOffTime = response.offTime;

  if (typeof directOnTime === "string" || typeof directOffTime === "string") {
    return {
      schedulers: {
        0: {
          onTime: String(directOnTime ?? "00:00:00"),
          offTime: String(directOffTime ?? "00:00:00"),
        },
        1: {
          onTime: String(directOnTime ?? "00:00:00"),
          offTime: String(directOffTime ?? "00:00:00"),
        },
      },
    };
  }

  const schedulers = (response.schedulers as Record<string, Record<string, unknown>> | null | undefined) ?? {};

  return {
    schedulers: Object.fromEntries(
      Object.entries(schedulers).map(([key, value]) => [
        key,
        {
          onTime: typeof value.onTime === "string"
            ? value.onTime
            : mapTimeSpanToTimeString(value.onTime as BigClock_Domain_Time | null | undefined),
          offTime: typeof value.offTime === "string"
            ? value.offTime
            : mapTimeSpanToTimeString(value.offTime as BigClock_Domain_Time | null | undefined),
        }
      ])
    ),
  };
};

export const mapTimeValueToTimeString = (time: unknown): string => {
  if (typeof time === "string") {
    return time;
  }

  return mapTimeSpanToTimeString(time as BigClock_Domain_Time | null | undefined);
};

export const mapRelayCoordinatesResponse = (response: Record<string, unknown>): Coordinates => ({
  coordinateSource: (response.coordinateSource as Coordinates["coordinateSource"] | undefined) ?? "Manual",
  latitudeDirection: String(response.latitudeDirection ?? "N"),
  latitude: String(response.latitude ?? ""),
  longitudeDirection: String(response.longitudeDirection ?? "E"),
  longitude: String(response.longitude ?? ""),
});

export const mapConnectedExternalDevice = (device: Record<string, unknown>): ConnectedDevice => ({
  address: String(device.address ?? ""),
  type: String(device.type ?? ""),
  typeName: "",
  status: String(device.status ?? ""),
  id: String(device.id ?? ""),
});

export const mapConnectedExternalDevicesResponse = (response: Record<string, unknown>): SystemDevices => {
  const mapGroup = (devices: unknown): ConnectedDevice[] => (
    Array.isArray(devices)
      ? devices.map((device) => mapConnectedExternalDevice(device as Record<string, unknown>))
      : []
  );

  return {
    dimmers: mapGroup(response.dimmers),
    electronicClocks: mapGroup(response.electronicClocks),
    ledPanels: mapGroup(response.ledPanels),
    clocks: mapGroup(response.clocks),
    ups: mapGroup(response.ups),
    sensors: mapGroup(response.sensors),
  };
};

export const mapElectronicClockParamsResponse = (response: Record<string, unknown>): ElectronicClockParams => {
  const displayDurations = (response.displayDurations as Record<string, unknown> | undefined) ?? {};

  return {
    displayEnabled: Boolean(response.displayEnabled),
    displayBrightness: Number(response.displayBrightness ?? 0),
    sensorTemperature: Number(response.sensorTemperature ?? 0),
    displayDurations: {
      time: Number(displayDurations.time ?? 0),
      date: Number(displayDurations.date ?? 0),
      temperature: Number(displayDurations.temperature ?? 0),
      storedTemperature: Number(displayDurations.storedTemperature ?? 0),
      storedPressure: Number(displayDurations.storedPressure ?? 0),
      storedHumidity: Number(displayDurations.storedHumidity ?? 0),
    }
  };
};

export const mapElectronicClockDataResponse = (response: Record<string, unknown>): ElectronicClockData => ({
  article: (response.article as string | null | undefined) ?? null,
  model: (response.model as string | null | undefined) ?? null,
  place: (response.place as string | null | undefined) ?? null,
  fabricDate: (response.fabricDate as string | null | undefined) ?? null,
  firmwareVersion: (response.firmwareVersion as string | number | null | undefined) ?? null,
  serialNumber: (response.serialNumber as string | null | undefined) ?? null,
});

export const mapDimmerSchedulerModeToContract = (
  mode: number | string | null | undefined
): Dimmer_SchedulerMode => {
  if (mode === 1 || mode === "Timer") {
    return "Timer";
  }

  if (mode === 2 || mode === "DuskDawn") {
    return "DuskDawn";
  }

  return "Manual";
};

export const mapDimmerSchedulerModeToLegacy = (
  mode: number | string | null | undefined
): number => {
  if (mode === 1 || mode === "Timer") {
    return 1;
  }

  if (mode === 2 || mode === "DuskDawn") {
    return 2;
  }

  return 0;
};

export const mapDimmerParamsResponse = (response: Record<string, unknown>): DimmerParams => {
  const dimmer = (response.dimmer as Record<string, unknown> | undefined) ?? {};
  const effects = (response.effects as Record<string, unknown> | undefined) ?? {};
  const relay = (response.relay as Record<string, unknown> | undefined) ?? {};

  return {
    dimmer: {
      lightEnabled: Boolean(dimmer.lightEnabled),
      fadeDivider: Number(dimmer.fadeDivider ?? 0),
      channels: Array.isArray(dimmer.channels)
        ? dimmer.channels.map((channel) => {
            const currentChannel = channel as Record<string, unknown>;

            return {
              isEnabled: Boolean(currentChannel.isEnabled),
              brightness: Number(currentChannel.brightness ?? 0),
              voltage: Number(currentChannel.voltage ?? 0),
              isActive: Boolean(currentChannel.isActive),
            };
          })
        : [],
      schedulerMode: mapDimmerSchedulerModeToContract(
        dimmer.schedulerMode as string | number | null | undefined
      ),
    },
    effects: {
      effectsAlwaysEnabled: Boolean(effects.effectsAlwaysEnabled),
      effectDelay: Number(effects.effectDelay ?? 0),
    },
    relay: {
      isActive: Boolean(relay.isActive),
      relayEnabled: Boolean(relay.relayEnabled),
      relayMode: mapDimmerSchedulerModeToLegacy(
        relay.relayMode as string | number | null | undefined
      ),
    },
  };
};

export const mapDimmerSchedulerTimeResponse = (response: Record<string, unknown>) => ({
  onTime: mapTimeValueToTimeString(
    response.enableTime ?? response.onTime
  ),
  offTime: mapTimeValueToTimeString(
    response.disableTime ?? response.offTime
  ),
});

export const mapControlBlockTimeResponse = (response: Record<string, unknown>) => ({
  time: (response.time as string | null | undefined) ?? "",
  dawnTime: mapTimeValueToTimeString(response.dawnTime),
  duskTime: mapTimeValueToTimeString(response.duskTime),
});
